import React, { useEffect, useRef, useState } from 'react';

import { Button, makeStyles, Menu, MenuItem } from '@material-ui/core';
import i18n from 'i18n-js';

import { ReservationDTO } from 'api/controllers/reservations';
import { TableMaps } from 'api/controllers/tableMaps';
import { PopupConfirmation } from 'components/shared';
import { theme } from 'config/theme';
import { formatGuestName, getNoShowAvailability, getTimePart } from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { ReservableTable } from '../hooks';

const useStyles = makeStyles({
    option: {
        flexDirection: 'column',
        gap: 6,
        minWidth: 260,
        padding: '10px 24px',
        '&.Mui-disabled': { opacity: 1 },
    },
    pill: {
        minWidth: 100,
        textAlign: 'center',
        fontSize: 14,
        borderRadius: 100,
        border: `1px solid ${theme.palette.error.main}`,
        color: theme.palette.error.main,
        '.Mui-disabled &': {
            borderColor: theme.palette.text.disabled,
            color: theme.palette.text.disabled,
        },
    },
    hint: { fontSize: 12, color: theme.palette.text.disabled },
});

export default function ConfirmedReservationActions({
    reservation,
    tables,
}: {
    reservation: ReservationDTO;
    tables: ReservableTable[];
}) {
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    const timeZone = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.timeZone
    );
    const [now, setNow] = useState(() => new Date());
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [isSubmitting, setSubmitting] = useState(false);
    const submitting = useRef(false);
    const mounted = useRef(true);
    const [loadedTables, setLoadedTables] = useState<{
        restaurantId: string;
        names: Record<string, string>;
    }>({ restaurantId: reservation.restaurantId, names: {} });
    const assignedNames = reservation.tables.map(
        (id) =>
            tables.find((table) => table.id === id)?.label ||
            (loadedTables.restaurantId === reservation.restaurantId ? loadedTables.names[id] : '')
    );
    const missingIds = reservation.tables.filter((_, index) => !assignedNames[index]);
    const missingKey = JSON.stringify(missingIds);
    const hasTableNames = reservation.tables.length > 0 && missingIds.length === 0;
    const isMenuOpen = !!anchor;
    const availability = getNoShowAvailability(reservation.reservationStartDateTime, timeZone, now);

    useEffect(() => {
        if (!isMenuOpen || missingKey === '[]') {
            return;
        }
        let cancelled = false;
        const ids: string[] = JSON.parse(missingKey);
        Promise.all(ids.map((id) => TableMaps.getMapTable(id)))
            .then((assignedTables) => {
                if (cancelled) {
                    return;
                }
                if (assignedTables.some((table) => !table.name?.trim())) {
                    throw new Error(i18n.t('reservations.tableNamesUnavailable'));
                }
                setLoadedTables((previous) => ({
                    restaurantId: reservation.restaurantId,
                    names: assignedTables.reduce<Record<string, string>>(
                        (names, table) => ({ ...names, [table.id]: table.name }),
                        previous.restaurantId === reservation.restaurantId ? previous.names : {}
                    ),
                }));
            })
            .catch((error) => {
                if (!cancelled) {
                    dispatch(globalStore.actionCreators.showToaster('error', error));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isMenuOpen, missingKey, reservation.restaurantId, dispatch]);

    useEffect(() => {
        const timer = window.setInterval(() => setNow(new Date()), 1000);
        return () => {
            mounted.current = false;
            window.clearInterval(timer);
        };
    }, []);

    async function markNoShow() {
        if (submitting.current || !hasTableNames) {
            return;
        }
        submitting.current = true;
        setSubmitting(true);
        try {
            await dispatch(reservationsStore.actionCreators.markNoShow(reservation.id));
            dispatch(
                globalStore.actionCreators.showToaster(
                    'success',
                    i18n.t('reservations.markedNoShow')
                )
            );
        } catch (error) {
            dispatch(globalStore.actionCreators.showToaster('error', error));
        } finally {
            submitting.current = false;
            if (mounted.current) {
                setSubmitting(false);
                setDialogOpen(false);
            }
        }
    }

    const tableNames = assignedNames.join(' + ');

    return (
        <>
            <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={(event) => {
                    setNow(new Date());
                    setAnchor(event.currentTarget);
                }}
            >
                {i18n.t('reservations.more')}
            </Button>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={() => setAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MenuItem
                    className={classes.option}
                    disabled={!availability.isAvailable || isSubmitting || !hasTableNames}
                    onClick={() => {
                        if (!availability.isAvailable || isSubmitting || !hasTableNames) {
                            return;
                        }
                        setAnchor(null);
                        setDialogOpen(true);
                    }}
                >
                    <span className={classes.pill}>{i18n.t('reservations.noShow')}</span>
                    {!availability.isAvailable && (
                        <span className={classes.hint}>
                            {i18n.t('reservations.noShowAvailableFrom', {
                                time: availability.availableFrom,
                            })}
                        </span>
                    )}
                    {!hasTableNames && (
                        <span className={classes.hint}>
                            {i18n.t('reservations.tableNamesUnavailable')}
                        </span>
                    )}
                </MenuItem>
            </Menu>
            <PopupConfirmation
                open={isDialogOpen && hasTableNames}
                isDismissible
                isSubmitting={isSubmitting}
                keepOpenOnAction
                actionVariant="contained"
                close={() => setDialogOpen(false)}
                title={i18n.t('reservations.noShowTitle')}
                description={i18n.t(
                    reservation.tables.length > 1
                        ? 'reservations.noShowDescriptionMany'
                        : 'reservations.noShowDescriptionOne',
                    {
                        name: formatGuestName(reservation),
                        time: getTimePart(reservation.reservationStartDateTime),
                        guests: reservation.guestsNumber,
                        tables: tableNames,
                    }
                )}
                activeBtn={i18n.t('reservations.markNoShow')}
                action={markNoShow}
            />
        </>
    );
}
